'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { heartBeat } from '@/lib/health';

export const HEART_BEAT_INTERVAL = 1000 * 60;

interface HeartBeatContextData {
  lastHeartBeat?: number;
  heartBeat: boolean;
}

const HeartBeatContext = createContext<HeartBeatContextData>({
  heartBeat: false,
});

export const useHeartBeat = () => {
  return useContext(HeartBeatContext);
};

export interface HeartBeatProps {
  children: ReactNode;
}

function HeartBeat({ children }: HeartBeatProps) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [heartBeatContextData, setHeartBeatContextData] =
    useState<HeartBeatContextData>({ heartBeat: false });

  const callHeartBeat = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    heartBeat()
      .then(() => {
        setHeartBeatContextData({ heartBeat: true, lastHeartBeat: Date.now() });
      })
      .catch(() => {
        setHeartBeatContextData({
          heartBeat: false,
          lastHeartBeat: Date.now(),
        });
      });

    intervalRef.current = setInterval(callHeartBeat, HEART_BEAT_INTERVAL);
  };

  useEffect(() => {
    callHeartBeat();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <HeartBeatContext.Provider value={heartBeatContextData}>
      {children}
    </HeartBeatContext.Provider>
  );
}

export { HeartBeat };
export default HeartBeat;
